import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Input from '../../../components/Input/Input';
import { Button } from 'src/components/ui/button';
import { useAuth } from 'src/provider/AuthProvider';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { passwordConfirmScheme, passwordScheme, stringScheme } from 'src/utils/validation-schemes';
import { useLoader } from 'src/provider/LoaderProvider';
import { PATH_HOME, PATH_ONBOARDING, PATH_REGISTER } from 'src/constants/spa-routes';
import { useUserRegistrationMutation } from 'src/services/usersSsoService';
import { getLeadSourceCampaign, getLeadSourceName, getLeadSourceType } from 'src/utils/utm.utils';
import { setBigin } from 'src/store/slices/onboardingSlice';
import { useAppSelector, useAppDispatch } from 'src/store';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const RegisterForm = () => {
  const [biginRequest, setBiginRequest] = React.useState(null);
  const navigate = useNavigate();
  const loader = useLoader();
  const { setUser } = useAuth();
  const dispatch = useAppDispatch();
  const { email } = useAppSelector((s) => s.registerSlice);

  const [
    userRegistration,
    {
      data: userRegistrationData,
      isLoading: isLoadingUserRegistration,
      isSuccess: isSuccessUserRegistration,
    },
  ] = useUserRegistrationMutation();

  useLoaderWhile(isLoadingUserRegistration);

  useEffect(() => {
    if (isSuccessUserRegistration && biginRequest) {
      const skipOnboarding = sessionStorage.getItem('skip_onboarding');

      if (globalThis.gtag) {
        globalThis.gtag('event', 'signup_success');
        globalThis.gtag('event', 'conversion', { send_to: 'AW-11237889467/1lznCL2ozOgbELur0u4p' });
      }

      if (skipOnboarding) {
        navigate(PATH_HOME.ROOT, { replace: true });
      } else {
        (async () => {
          const response = await fetch('/api/bigin', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(biginRequest),
          }).then((res) => res.json());

          if (response.success) {
            dispatch(setBigin({ pipelineId: response.pipelineId, data: biginRequest }));
          }

          navigate(PATH_ONBOARDING.ROOT, { replace: true });
        })();
      }
    }
  }, [isSuccessUserRegistration, biginRequest]);

  useEffect(() => {
    if (userRegistrationData && isSuccessUserRegistration) {
      const storedUser = localStorage.getItem('user');
      const user: Record<string, unknown> = {
        ...(storedUser && storedUser !== 'undefined' && JSON.parse(storedUser)),
        ...userRegistrationData,
      };

      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [userRegistrationData]);

  const {
    handleSubmit,
    trigger,
    control,
    clearErrors,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      password: '',
      passwordConfirm: '',
      firstName: '',
      lastName: '',
      companyName: '',
    },
    resolver: formResolver(
      object({
        firstName: stringScheme({ required: true }).label('First name'),
        lastName: stringScheme({ required: true }).label('Last name'),
        companyName: stringScheme({
          required: true,
          min: 3,
          minMessage: 'Min. 3 characters',
        }).label('Company name'),
        password: passwordScheme({
          required: true,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Password'),
        passwordConfirm: passwordConfirmScheme({
          required: true,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Password Confirm'),
      }),
    ),
  });

  const submit = async (data: {
    password: string;
    passwordConfirm: string;
    firstName: string;
    lastName: string;
    companyName: string;
  }) => {
    if (isValid) {
      loader.show();

      const newBiginRequest = {
        email: email,
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
        leadSourceName: getLeadSourceName(),
        leadSourceType: getLeadSourceType(),
        leadSourceCampaign: getLeadSourceCampaign(),
        sourceUrl: globalThis.location.href,
        tags: ['Cabinet'],
      };

      setBiginRequest(newBiginRequest);

      await userRegistration({
        email: email,
        password: data.password,
        password2: data.passwordConfirm,
        first_name: data.firstName,
        last_name: data.lastName,
        company_name: data.companyName,
        company_email: email,
      });
    }
  };

  return (
    <React.Fragment>
      <form
        id="registration-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(submit)(e);
        }}
      >
        <div className={'input-container flex flex-col gap-[3px]'}>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                autoFocus
                type={'text'}
                label={'First Name'}
                placeholder={'Josh'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('firstName')}
                onFocus={() => clearErrors('firstName')}
              />
            )}
          />
        </div>
        <div className={'input-container flex flex-col gap-[3px] mt-[3px]'}>
          <Controller
            control={control}
            name="lastName"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Last Name'}
                placeholder={'Brawn'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('lastName')}
                onFocus={() => clearErrors('lastName')}
              />
            )}
          />
        </div>
        <div className={'input-container flex flex-col gap-[3px] mt-[3px]'}>
          <Controller
            control={control}
            name="companyName"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Company Name'}
                placeholder={'Company'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('companyName')}
                onFocus={() => clearErrors('companyName')}
              />
            )}
          />
        </div>
        <div className={'input-container flex flex-col gap-[3px] mt-[3px]'}>
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'password'}
                label={'Password'}
                placeholder={'••••••••••••••••••••••••••••'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onFocus={() => clearErrors('password')}
              />
            )}
          />
        </div>
        <div className={'input-container flex flex-col gap-[3px] mt-[3px]'}>
          <Controller
            control={control}
            name="passwordConfirm"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'password'}
                label={'Confirm password'}
                placeholder={'••••••••••••••••••••••••••••'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onFocus={() => clearErrors('passwordConfirm')}
              />
            )}
          />
        </div>
      </form>
      <div className="flex flex-col">
        <Button
          form="registration-form"
          type="submit"
          className="mt-[25px] w-full min-h-[50px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]"
        >
          <div>Continue</div>
        </Button>
        <div>
          <p className={'text-center pt-5'}>
            <Link className={'text-xs text-[#3F60B3] hover:underline'} to={PATH_REGISTER.SIGN_UP}>
              ← Back
            </Link>
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};
